import { type JSX } from 'react';
// eslint-disable-next-line no-restricted-imports
import type { GroupHeadingProps, MenuPosition } from 'react-select';
import { components } from 'react-select';
import { racClauseSelectors } from '@gonfalon/dogfood-flags/dogfood-flags';
import { List } from '@gonfalon/immutable-shim/immutableShim';
import type { OptionProps, StylesObject } from '@gonfalon/launchpad-experimental/CustomSelect';
import { CustomSelect } from '@gonfalon/launchpad-experimental/CustomSelect';
import { useParam } from '@gonfalon/router/useParam';
import {
  Collection,
  ComboBox,
  Group,
  Header,
  IconButton,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  ListBoxSection,
  Popover,
  ProgressBar,
  Text,
} from '@launchpad-ui/components';
// oxlint-disable-next-line no-restricted-imports
import { Label as LegacyLabel, Tooltip as LegacyTooltip } from '@launchpad-ui/core';
import { v4 } from 'uuid';

import type { StrictGroupedOption } from '#components/Contexts/utils/contextTargetingUtils';
import { makeSelectContextKindOptions } from '#components/Contexts/utils/contextTargetingUtils';
import { useFlagContext } from '#components/FlagContext';
import { FlagContextErrorWrapper } from '#components/FlagContextErrorWrapper';
import { useContextKinds } from '#hooks/useContextKinds';
import { CONTEXT_KIND_ATTRIBUTE } from '#utils/constants';

import styles from './SelectContextKind.module.css';

export type BaseSelectContextKindProps = {
  className?: string;
  id?: string;
  value?: string;
  projectKey?: string;
  flagKey?: string;
  name?: string;
  disabled?: boolean;
  label?: string | JSX.Element;
  menuPosition?: MenuPosition;
  onChange?(contextKind: string): void;
  /**
   * @deprecated New RAC-based selector does not use this prop
   */
  onBlur?(event: React.FocusEvent<HTMLInputElement>): void;
  /**
   * When true, excludes the "Context kind" meta-attribute from the dropdown options.
   * Use this when only actual context kinds should be selectable (e.g., rollout bucket-by).
   */
  excludeKindAttribute?: boolean;
};

const customSelectStyleOverrides: StylesObject = {
  input: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  group: {
    paddingBottom: '0',
    paddingTop: '0',
  },
  groupHeading: {
    backgroundColor: 'var(--lp-color-bg-ui-secondary)',
    color: 'var(--lp-color-text-ui-secondary)',
    fontSize: 'var(--lp-font-size-200)',
    letterSpacing: '1px',
    textTransform: 'none',
    paddingTop: 'var(--lp-spacing-300)',
    paddingBottom: 'var(--lp-spacing-300)',
    borderTop: '1px solid var(--lp-color-border-ui-primary)',
  },
};

const GroupOptionHeading = (props: GroupHeadingProps<StrictGroupedOption>) => {
  if (!props.data.label) {
    return <components.GroupHeading style={{ display: 'none' }} {...props} />;
  } else {
    return <components.GroupHeading {...props} />;
  }
};

const BaseSelectContextKind = ({
  className,
  id = v4(),
  value: selectedContextKindValue,
  name,
  flagKey,
  disabled,
  menuPosition,
  label,
  onBlur,
  onChange,
  projectKey,
  excludeKindAttribute,
}: BaseSelectContextKindProps) => {
  const { unhiddenContextKinds, isReady } = useContextKinds({ projectKey });

  const { flagConfiguration } = useFlagContext();
  const contextKindsToDisplay = unhiddenContextKinds;
  const flagEvaluatedContextKindKeys = (flagKey && flagConfiguration?.evaluation?.contextKinds) || List<string>();

  const optionsResult = makeSelectContextKindOptions({
    contextKinds: contextKindsToDisplay,
    evaluatedContextKindKeys: flagEvaluatedContextKindKeys,
    excludeKindAttribute,
  });

  const flattedOptions = optionsResult.groupedOptions
    ? optionsResult.options.reduce<Array<{ value: string; label: string }>>(
        (accum, currStrictGroupedOption) => [...accum, ...currStrictGroupedOption.options],
        [],
      )
    : optionsResult.options;
  let selectedOption: { label: string; value: string } | undefined;

  if (selectedContextKindValue) {
    selectedOption = flattedOptions.find((o) => o.value === selectedContextKindValue) || {
      label: selectedContextKindValue,
      value: selectedContextKindValue,
    };
  }

  const renderItem = (item: { value: string; label: string }) => (
    <ListBoxItem key={item.value} id={item.value} value={item} textValue={item.label}>
      <Text slot="label">{item.label}</Text>
      {item.value === CONTEXT_KIND_ATTRIBUTE ? <Text slot="description">Option for matching context kinds</Text> : null}
    </ListBoxItem>
  );

  const renderGroup = (groupedOption: StrictGroupedOption) => (
    <ListBoxSection
      key={groupedOption.label}
      id={groupedOption.label}
      items={groupedOption.options}
      aria-label={groupedOption.label}
    >
      <Header>{groupedOption.label}</Header>
      <Collection items={groupedOption.options}>{renderItem}</Collection>
    </ListBoxSection>
  );

  // I couldn't figure out how to make ListBox accept a generic union of props (items + matching children render function)
  const listbox = optionsResult.groupedOptions ? (
    <ListBox
      aria-label="Context Kinds"
      renderEmptyState={() => (isReady ? 'No values found' : 'Loading values')}
      items={optionsResult.options}
    >
      {renderGroup}
    </ListBox>
  ) : (
    <ListBox
      aria-label="Context Kinds"
      renderEmptyState={() => (isReady ? 'No values found' : 'Loading values')}
      items={optionsResult.options}
    >
      {renderItem}
    </ListBox>
  );

  if (racClauseSelectors()) {
    return (
      <>
        <ComboBox
          id={id}
          name={name}
          className={className}
          isDisabled={disabled}
          onSelectionChange={(key) => {
            // Ignore nulls and numbers (numbers dont exist but TS thinks they do)
            if (typeof key === 'string') {
              onChange?.(key);
            }
          }}
          selectedKey={selectedOption?.value}
          aria-label="Select a context kind"
        >
          {label ? <Label>{label}</Label> : null}
          <Group>
            <Input aria-label="Select context kind" placeholder="Select a context kind" />
            {!isReady ? <ProgressBar isIndeterminate aria-label="Loading context kinds" /> : null}
            <IconButton icon="chevron-down" size="small" variant="minimal" aria-label="Pick context kind" />
          </Group>
          <Popover placement="bottom" aria-label="Select a context kind">
            {listbox}
          </Popover>
        </ComboBox>
      </>
    );
  }
  return (
    <>
      {label ? <LegacyLabel htmlFor={id}>{label}</LegacyLabel> : null}
      <CustomSelect
        className={className}
        id={id}
        name={name}
        value={selectedOption}
        options={optionsResult.options}
        onChange={(option: OptionProps) => onChange?.(option.value)}
        onBlur={onBlur}
        placeholder="Select a context kind"
        ariaLabel="Select context kind"
        styles={customSelectStyleOverrides}
        noOptionsMessage={() => (!isReady ? 'Loading values' : 'No values found')}
        isLoading={!isReady}
        disabled={disabled}
        formatOptionLabel={formatOptionLabel}
        customComponents={{ GroupHeading: GroupOptionHeading }}
        menuPosition={menuPosition}
      />
    </>
  );
};

const formatOptionLabel = (option: OptionProps) => {
  const optionLabel = <div className="SelectContextKind-option">{option.label}</div>;

  if (option.value !== CONTEXT_KIND_ATTRIBUTE) {
    return optionLabel;
  }

  return (
    <LegacyTooltip targetClassName={styles.tooltip} placement="top" content="Option for matching context kinds">
      {optionLabel}
    </LegacyTooltip>
  );
};

export type SelectContextKindProps = Omit<BaseSelectContextKindProps, 'flagKey'>;

export const SelectContextKind = (props: SelectContextKindProps) => {
  const params = {
    envKey: useParam('envKey', { optional: true }),
    flagKey: useParam('flagKey', { optional: true }),
  };

  const ContextKindsModal = <BaseSelectContextKind {...props} flagKey={params.flagKey} />;

  return params.flagKey && params.envKey ? (
    <FlagContextErrorWrapper envKey={params.envKey} flagKey={params.flagKey} expandEvaluation>
      {ContextKindsModal}
    </FlagContextErrorWrapper>
  ) : (
    ContextKindsModal
  );
};
